import { apiClient } from './api.service';

export interface SystemStats {
  users: {
    total: number;
    active: number;
    inactive: number;
    admins: number;
  };
  components: {
    total: number;
  };
  risks: {
    total: number;
    byStatus: Record<string, number>;
  };
  spaces: {
    total: number;
    public: number;
    private: number;
  };
  teams: {
    total: number;
  };
}

export interface AuditLogEntry {
  id: string;
  action: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  targetEntityType?: string;
  targetEntityId?: string;
  targetEntityName?: string;
  previousValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  description?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AuditLogResponse {
  logs: AuditLogEntry[];
  total: number;
}

export interface AuditLogQuery {
  action?: string;
  userId?: string;
  targetEntityType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface RecalculateResult {
  processed: number;
  errors: number;
}

export interface ExportData {
  users: unknown[];
  teams: unknown[];
  spaces: unknown[];
  components: unknown[];
  risks: unknown[];
  exportedAt: string;
}

export interface HealthStatus {
  database: boolean;
  neo4j: boolean;
  uptime: number;
  version: string;
}

/**
 * Get system statistics
 */
export const getStats = async (): Promise<SystemStats> => {
  return apiClient.get<SystemStats>('/admin/stats');
};

/**
 * Get audit log
 */
export const getAuditLog = async (
  query?: AuditLogQuery
): Promise<AuditLogResponse> => {
  const params = new globalThis.URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
  }
  const queryString = params.toString();
  return apiClient.get<AuditLogResponse>(
    `/admin/audit-log${queryString ? `?${queryString}` : ''}`
  );
};

/**
 * Get recent admin activity
 */
export const getRecentActivity = async (
  limit?: number
): Promise<AuditLogEntry[]> => {
  const queryString = limit ? `?limit=${limit}` : '';
  return apiClient.get<AuditLogEntry[]>(`/admin/recent-activity${queryString}`);
};

/**
 * Recalculate all risk scores
 */
export const recalculateScores = async (): Promise<RecalculateResult> => {
  return apiClient.post<RecalculateResult>('/admin/recalculate-scores', {});
};

/**
 * Export system data
 */
export const exportData = async (): Promise<ExportData> => {
  return apiClient.get<ExportData>('/admin/export');
};

/**
 * Get health check status
 */
export const getHealthCheck = async (): Promise<HealthStatus> => {
  return apiClient.get<HealthStatus>('/admin/health-check');
};

export const adminService = {
  getStats,
  getAuditLog,
  getRecentActivity,
  recalculateScores,
  exportData,
  getHealthCheck,
};
